import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import PageHeader from '../../components/shell/PageHeader';
import DataTable from '../../components/list/DataTable';
import { SavedViewTabs } from '../../components/list/ListScreen';
import StatusPill from '../../components/ui/StatusPill';
import Modal from '../../components/overlays/Modal';
import { Field, Select, TextInput } from '../../components/ui/Field';
import { useOverlays } from '../../components/overlays/OverlayProvider';
import {
  createHeroSection,
  createPageHeader,
  deleteHeroSection,
  deletePageFooter,
  deletePageHeader,
  getAllHeroSections,
  getAllPageFooters,
  getAllPageHeaders,
} from '../../actions/bannersAction';
import { getAllPages, getAllPageSections, updatePageSections } from '../../actions/pageSectionAssignAction';
import { formatDate } from '../../helpers/format';
import './growth.css';

const VIEWS = [
  { key: 'hero', label: 'Hero sections' },
  { key: 'header', label: 'Page headers' },
  { key: 'footer', label: 'Page footers' },
  { key: 'pages', label: 'Page assignment' },
];

const PLATFORMS = [
  { value: 'web', label: 'Web' },
  { value: 'mobile', label: 'Mobile' },
  { value: 'all', label: 'All platforms' },
];

const EMPTY = {
  platform: 'web',
  redirect_url: '',
  reference_label: '',
  start_date: '',
  end_date: '',
  country: 'IN',
  web_image: null,
  mobile_image: null,
};

export default function Banners() {
  const dispatch = useDispatch();
  const { confirm, toast } = useOverlays();
  const [view, setView] = useState(0);
  const [rows, setRows] = useState([]);
  const [pages, setPages] = useState([]);
  const [sections, setSections] = useState({ headers: [], footers: [], hero_sections: [] });
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [assigning, setAssigning] = useState(null);

  const kind = VIEWS[view].key;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      if (kind === 'pages') {
        const [pageList, sectionList] = await Promise.all([
          getAllPages(dispatch),
          getAllPageSections(dispatch),
        ]);
        setPages(Array.isArray(pageList) ? pageList : []);
        setSections(sectionList || { headers: [], footers: [], hero_sections: [] });
      } else {
        const loader =
          kind === 'hero' ? getAllHeroSections : kind === 'header' ? getAllPageHeaders : getAllPageFooters;
        const data = await loader(dispatch);
        setRows(Array.isArray(data) ? data : data?.data || []);
      }
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [dispatch, kind]);

  useEffect(() => {
    load();
  }, [load]);

  const remove = useCallback(
    async (row) => {
      const ok = await confirm({
        title: `Delete “${row.reference_label || row.title}”?`,
        body: 'It is removed from every page it is assigned to, immediately. This cannot be undone.',
        confirmLabel: 'Delete',
        destructive: true,
      });
      if (!ok) return;
      const remover =
        kind === 'hero' ? deleteHeroSection : kind === 'header' ? deletePageHeader : deletePageFooter;
      try {
        await remover(dispatch, row.id);
        load();
      } catch {
        toast('Could not delete it', { tone: 'error' });
      }
    },
    [confirm, dispatch, kind, toast, load]
  );

  const create = useCallback(async () => {
    setBusy(true);
    const data = new FormData();
    data.append('platform', form.platform);
    data.append('redirect_url', form.redirect_url);
    data.append('reference_label', form.reference_label);
    data.append('start_date', form.start_date);
    data.append('end_date', form.end_date);
    data.append('country', form.country);
    if (form.web_image) data.append('web_image_url', form.web_image);
    if (form.mobile_image) data.append('mobile_image_url', form.mobile_image);

    try {
      await (kind === 'hero' ? createHeroSection : createPageHeader)(data, dispatch);
      toast('Created', { tone: 'success' });
      setCreating(false);
      setForm(EMPTY);
      load();
    } catch {
      toast('Could not create it', { tone: 'error' });
    } finally {
      setBusy(false);
    }
  }, [dispatch, form, kind, toast, load]);

  const saveAssignment = useCallback(async () => {
    setBusy(true);
    try {
      const payload = [];
      if (assigning.header) payload.push({ section: 'header', ids: [assigning.header] });
      if (assigning.hero) payload.push({ section: 'hero', ids: [assigning.hero] });
      if (assigning.footer) payload.push({ section: 'footer', id: assigning.footer });
      await updatePageSections(assigning.id, payload, dispatch);
      toast('Page sections updated', { tone: 'success' });
      setAssigning(null);
      load();
    } catch {
      toast('Could not update the page', { tone: 'error' });
    } finally {
      setBusy(false);
    }
  }, [assigning, dispatch, toast, load]);

  const sectionColumns = useMemo(
    () => [
      {
        key: 'preview',
        header: '',
        width: 90,
        render: (r) =>
          r.web_image_url ? (
            <img src={r.web_image_url} alt="" className="sv-banner-thumb" />
          ) : (
            <span className="sv-banner-thumb is-empty" />
          ),
      },
      { key: 'reference_label', header: 'Label', width: 240, render: (r) => r.reference_label || r.title || '—' },
      { key: 'platform', header: 'Placement', width: 130, muted: true, render: (r) => r.platform || '—' },
      {
        key: 'dates',
        header: 'Runs',
        width: 220,
        muted: true,
        render: (r) => `${formatDate(r.start_date)} – ${formatDate(r.end_date)}`,
      },
      {
        key: 'status',
        header: 'Status',
        width: 120,
        render: (r) => {
          const now = Date.now();
          const start = new Date(r.start_date).getTime();
          const end = new Date(r.end_date).getTime();
          const tone = now < start ? 'review' : now > end ? 'neutral' : 'live';
          const label = now < start ? 'Scheduled' : now > end ? 'Expired' : 'Live';
          return <StatusPill tone={tone}>{label}</StatusPill>;
        },
      },
    ],
    []
  );

  const pageColumns = useMemo(
    () => [
      { key: 'name', header: 'Page', width: 220 },
      {
        key: 'header',
        header: 'Header',
        muted: true,
        render: (r) => (r.header || []).map((h) => h.name).join(', ') || '—',
      },
      {
        key: 'hero',
        header: 'Hero',
        muted: true,
        render: (r) => (r.hero || []).map((h) => h.name).join(', ') || '—',
      },
      { key: 'footer', header: 'Footer', muted: true, render: (r) => r.footer?.name || '—' },
    ],
    []
  );

  const isPages = kind === 'pages';
  const canCreate = kind === 'hero' || kind === 'header';

  return (
    <>
      <PageHeader
        section="Growth"
        title="Banners & sections"
        subtitle="Hero banners, page headers and footers, and which pages they appear on."
        primaryAction={
          canCreate
            ? { label: 'New section', icon: 'bx-plus', onClick: () => setCreating(true) }
            : undefined
        }
      />

      <div className="sv-screen-body">
        <SavedViewTabs views={VIEWS} activeView={view} onChange={setView} counts={{}} />

        <DataTable
          columns={isPages ? pageColumns : sectionColumns}
          rows={isPages ? pages : rows}
          rowKey={(r) => r.id}
          loading={loading}
          selectable={false}
          noun={isPages ? 'pages' : 'sections'}
          onRowClick={
            isPages
              ? (r) =>
                  setAssigning({
                    id: r.id,
                    name: r.name,
                    header: r.header?.[0]?.id || '',
                    hero: r.hero?.[0]?.id || '',
                    footer: r.footer?.id || '',
                  })
              : undefined
          }
          onRowAction={isPages ? undefined : (r, e) => { e.stopPropagation(); remove(r); }}
          empty={{
            icon: 'bx-image-add',
            title: isPages ? 'No pages configured' : 'Nothing here yet',
            description: isPages
              ? 'Pages are defined in the app and appear here once created.'
              : 'Create a section to show it on the site.',
          }}
        />
      </div>

      <Modal
        open={creating}
        onClose={() => setCreating(false)}
        title={kind === 'hero' ? 'New hero section' : 'New page header'}
        size="form"
        confirmLabel="Create"
        confirmLoading={busy}
        confirmDisabled={!form.reference_label.trim() || !form.start_date || !form.end_date}
        onConfirm={create}
      >
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, paddingTop: 4 }}>
          <Field label="Reference label" required className="sv-span-2">
            <TextInput
              value={form.reference_label}
              onChange={(e) => setForm({ ...form, reference_label: e.target.value })}
              placeholder="Internal name, e.g. Diwali hero"
            />
          </Field>
          <Field label="Platform">
            <Select options={PLATFORMS} value={form.platform} onChange={(e) => setForm({ ...form, platform: e.target.value })} />
          </Field>
          <Field label="Country">
            <TextInput value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} />
          </Field>
          <Field label="Redirect URL" className="sv-span-2">
            <TextInput value={form.redirect_url} onChange={(e) => setForm({ ...form, redirect_url: e.target.value })} />
          </Field>
          <Field label="Starts" required>
            <TextInput type="datetime-local" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} />
          </Field>
          <Field label="Ends" required>
            <TextInput type="datetime-local" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} />
          </Field>
          <Field label="Web image">
            <input type="file" accept="image/*" onChange={(e) => setForm({ ...form, web_image: e.target.files[0] })} />
          </Field>
          <Field label="Mobile image">
            <input type="file" accept="image/*" onChange={(e) => setForm({ ...form, mobile_image: e.target.files[0] })} />
          </Field>
        </div>
      </Modal>

      <Modal
        open={!!assigning}
        onClose={() => setAssigning(null)}
        title={`Sections for ${assigning?.name || ''}`}
        size="form"
        confirmLabel="Save"
        confirmLoading={busy}
        onConfirm={saveAssignment}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, paddingTop: 4 }}>
          <Field label="Header">
            <Select
              options={(sections.headers || []).map((h) => ({ value: h.id, label: h.label }))}
              placeholder="None"
              value={assigning?.header || ''}
              onChange={(e) => setAssigning((a) => ({ ...a, header: e.target.value }))}
            />
          </Field>
          <Field label="Hero section">
            <Select
              options={(sections.hero_sections || []).map((h) => ({ value: h.id, label: h.label }))}
              placeholder="None"
              value={assigning?.hero || ''}
              onChange={(e) => setAssigning((a) => ({ ...a, hero: e.target.value }))}
            />
          </Field>
          <Field label="Footer">
            <Select
              options={(sections.footers || []).map((h) => ({ value: h.id, label: h.label }))}
              placeholder="None"
              value={assigning?.footer || ''}
              onChange={(e) => setAssigning((a) => ({ ...a, footer: e.target.value }))}
            />
          </Field>
        </div>
      </Modal>
    </>
  );
}
